/**
 * FinanzenDashboard.tsx — Finanzen-Übersichtsseite
 * 
 * DESIGN: Professionell, reduziert, Koop-Farbschema
 * DATEN: Echte Supabase-Tabellen (rechnungen, zahlungseingaenge, forderungsakten)
 * 
 * Features:
 * - KPI-Karten: Offene Rechnungen, Zahlungseingänge, Forderungen MW, Überfällige
 * - Mini-Chart: Umsatz letzte 6 Monate
 * - Schnellzugriff-Buttons: Neue Rechnung, DATEV-Export, Forderungen
 * - Letzte Rechnungen + Zahlungseingänge
 */

import { useState, useMemo } from 'react';
import { useLocation } from 'wouter';
import { useApp } from '@/contexts/AppContext';
import { useRechnungen, useZahlungseingaenge } from '@/hooks/useSupabase';
import { useForderungen } from '@/hooks/useForderungen';
import { supabase } from '@/lib/supabase';
import type { Rechnung, Zahlungseingang } from '@/lib/database.types';
import {
  Receipt, TrendingUp, AlertCircle, CreditCard, FileText, Plus,
  ArrowUpRight, ArrowDownRight, Clock, CheckCircle2, Download,
  Building2, BarChart3, Wallet, Scale, ChevronRight, ExternalLink,
  Loader2, DollarSign, PieChart, CalendarDays,
} from 'lucide-react';
import LiquiditaetDashboardWidget from '@/components/LiquiditaetDashboardWidget';

// ============================================================
// HELPERS
// ============================================================

function formatEuro(n: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n);
}

function formatDate(d: string): string {
  return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function getMonthLabel(monthsAgo: number): string {
  const d = new Date();
  d.setMonth(d.getMonth() - monthsAgo);
  return d.toLocaleDateString('de-DE', { month: 'short', year: '2-digit' });
}

// ============================================================
// MINI BAR CHART (SVG)
// ============================================================

function MiniBarChart({ data, height = 120 }: { data: { label: string; value: number }[]; height?: number }) {
  const maxVal = Math.max(...data.map(d => d.value), 1);
  const barWidth = 100 / data.length;
  const barGap = 8;

  return (
    <div className="w-full">
      <svg viewBox={`0 0 ${data.length * 60} ${height + 30}`} className="w-full" preserveAspectRatio="xMidYMid meet">
        {data.map((d, i) => {
          const barH = (d.value / maxVal) * height;
          const x = i * 60 + barGap / 2;
          const y = height - barH;
          return (
            <g key={i}>
              {/* Bar */}
              <rect
                x={x}
                y={y}
                width={60 - barGap}
                height={barH}
                rx={4}
                fill={i === data.length - 1 ? '#C41E3A' : '#E5E7EB'}
                className="transition-all duration-300"
              />
              {/* Value label */}
              {d.value > 0 && (
                <text
                  x={x + (60 - barGap) / 2}
                  y={y - 6}
                  textAnchor="middle"
                  className="text-[9px] fill-gray-500 font-medium"
                >
                  {d.value >= 1000 ? `${(d.value / 1000).toFixed(0)}k` : d.value.toFixed(0)}
                </text>
              )}
              {/* Month label */}
              <text
                x={x + (60 - barGap) / 2}
                y={height + 18}
                textAnchor="middle"
                className="text-[10px] fill-gray-400"
              >
                {d.label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ============================================================
// STATUS BADGE
// ============================================================

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  entwurf: { label: 'Entwurf', color: 'bg-gray-100 text-gray-500' },
  erstellt: { label: 'Erstellt', color: 'bg-blue-50 text-blue-600' },
  versendet: { label: 'Versendet', color: 'bg-blue-50 text-blue-600' },
  gesendet: { label: 'Gesendet', color: 'bg-blue-50 text-blue-600' },
  bezahlt: { label: 'Bezahlt', color: 'bg-emerald-50 text-emerald-600' },
  teilbezahlt: { label: 'Teilbezahlt', color: 'bg-amber-50 text-amber-600' },
  kuerzung: { label: 'Kürzung', color: 'bg-orange-50 text-orange-600' },
  ueberfaellig: { label: 'Überfällig', color: 'bg-red-50 text-red-600' },
  storniert: { label: 'Storniert', color: 'bg-gray-50 text-gray-400' },
  mahnung: { label: 'Mahnung', color: 'bg-red-50 text-red-700' },
};

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: 'bg-gray-100 text-gray-500' };
  return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${cfg.color}`}>{cfg.label}</span>;
}

// ============================================================
// KPI CARD
// ============================================================

function KPICard({ label, value, subtext, icon: Icon, color, trend, trendLabel }: {
  label: string;
  value: string;
  subtext?: string;
  icon: typeof Receipt;
  color: string;
  trend?: 'up' | 'down' | 'neutral';
  trendLabel?: string;
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-sm transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}12` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        {trend && (
          <div className={`flex items-center gap-0.5 text-xs font-medium ${
            trend === 'up' ? 'text-emerald-600' : trend === 'down' ? 'text-red-600' : 'text-gray-500'
          }`}>
            {trend === 'up' ? <ArrowUpRight className="w-3.5 h-3.5" /> : trend === 'down' ? <ArrowDownRight className="w-3.5 h-3.5" /> : null}
            {trendLabel}
          </div>
        )}
      </div>
      <div className="text-2xl font-bold text-gray-900 tracking-tight">{value}</div>
      <div className="text-xs text-gray-500 mt-0.5">{label}</div>
      {subtext && <div className="text-[10px] text-gray-400 mt-1">{subtext}</div>}
    </div>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function FinanzenDashboard() {
  const { data: rechnungenRaw, loading: rechnungenLoading } = useRechnungen();
  const { data: zahlungenRaw, loading: zahlungenLoading } = useZahlungseingaenge();
  const { forderungen, loading: forderungenLoading } = useForderungen();
  const rechnungen = rechnungenRaw || [];
  const zahlungen = zahlungenRaw || [];
  const [, setLocation] = useLocation();
  const { openForderungsmanager } = useApp();

  const loading = rechnungenLoading || zahlungenLoading || forderungenLoading;

  // ── KPI Berechnungen ──
  const kpis = useMemo(() => {
    const now = new Date();
    const thisMonth = now.toISOString().slice(0, 7); // YYYY-MM
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().slice(0, 7);

    // Offene Rechnungen (nicht bezahlt, nicht storniert)
    const offeneRechnungen = rechnungen.filter(r => !['bezahlt', 'storniert'].includes(r.status));
    const offeneBetrag = offeneRechnungen.reduce((s, r) => s + (r.brutto_betrag - r.bezahlt_betrag), 0);

    // Überfällige Rechnungen
    const today = now.toISOString().split('T')[0];
    const ueberfaellige = rechnungen.filter(r =>
      !['bezahlt', 'storniert'].includes(r.status) && r.faellig_am && r.faellig_am < today
    );
    const ueberfaelligBetrag = ueberfaellige.reduce((s, r) => s + (r.brutto_betrag - r.bezahlt_betrag), 0);

    // Zahlungseingänge diesen Monat
    const zahlungenDiesenMonat = zahlungen.filter(z => z.datum?.startsWith(thisMonth));
    const zahlungenBetrag = zahlungenDiesenMonat.reduce((s, z) => s + z.betrag, 0);

    // Zahlungseingänge letzten Monat (für Trend)
    const zahlungenLetztenMonat = zahlungen.filter(z => z.datum?.startsWith(lastMonth));
    const zahlungenLetzterMonatBetrag = zahlungenLetztenMonat.reduce((s, z) => s + z.betrag, 0);

    // Forderungen MW
    const offeneForderungen = forderungen.filter(f => !['erledigt', 'abgerechnet'].includes(f.status));
    const forderungenBetrag = offeneForderungen.reduce((s, f) => s + (f.gesamtbetrag || 0), 0);

    // Umsatz letzte 6 Monate (aus bezahlten Rechnungen)
    const umsatzMonate: { label: string; value: number }[] = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monatKey = d.toISOString().slice(0, 7);
      const monatLabel = d.toLocaleDateString('de-DE', { month: 'short' });
      const monatUmsatz = rechnungen
        .filter(r => r.status === 'bezahlt' && r.bezahlt_am?.startsWith(monatKey))
        .reduce((s, r) => s + r.brutto_betrag, 0);
      // Alternativ: Zahlungseingänge pro Monat
      const zahlungenMonat = zahlungen
        .filter(z => z.datum?.startsWith(monatKey))
        .reduce((s, z) => s + z.betrag, 0);
      umsatzMonate.push({ label: monatLabel, value: zahlungenMonat || monatUmsatz });
    }

    // Gesamtumsatz dieses Jahr
    const thisYear = now.getFullYear().toString();
    const jahresUmsatz = rechnungen
      .filter(r => r.status === 'bezahlt' && r.bezahlt_am?.startsWith(thisYear))
      .reduce((s, r) => s + r.brutto_betrag, 0);

    return {
      offeneRechnungen: offeneRechnungen.length,
      offeneBetrag,
      ueberfaellige: ueberfaellige.length,
      ueberfaelligBetrag,
      zahlungenDiesenMonat: zahlungenDiesenMonat.length,
      zahlungenBetrag,
      zahlungenTrend: zahlungenLetzterMonatBetrag > 0
        ? ((zahlungenBetrag - zahlungenLetzterMonatBetrag) / zahlungenLetzterMonatBetrag * 100).toFixed(0)
        : null,
      offeneForderungen: offeneForderungen.length,
      forderungenBetrag,
      umsatzMonate,
      jahresUmsatz,
    };
  }, [rechnungen, zahlungen, forderungen]);

  // ── Letzte Rechnungen ──
  const letzteRechnungen = useMemo(() => rechnungen.slice(0, 8), [rechnungen]);

  // ── Letzte Zahlungseingänge ──
  const letzteZahlungen = useMemo(() => zahlungen.slice(0, 6), [zahlungen]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Finanzen — Übersicht</h1>
          <p className="text-sm text-gray-500 mt-1">Rechnungen, Zahlungen, Forderungen auf einen Blick</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
          <button
            onClick={() => setLocation('/buchhaltung/rechnungen')}
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-[#C41E3A] text-white rounded-lg text-sm font-medium hover:bg-[#A3172F] transition-colors min-h-[44px]"
          >
            <Plus className="w-4 h-4" />
            Neue Rechnung
          </button>
          <button
            onClick={() => setLocation('/buchhaltung/datev')}
            className="flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors min-h-[44px]"
          >
            <Download className="w-4 h-4" />
            DATEV-Export
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPICard
          label="Offene Rechnungen"
          value={formatEuro(kpis.offeneBetrag)}
          subtext={`${kpis.offeneRechnungen} Rechnungen offen`}
          icon={Receipt}
          color="#2563EB"
        />
        <KPICard
          label="Zahlungseingänge (Monat)"
          value={formatEuro(kpis.zahlungenBetrag)}
          subtext={`${kpis.zahlungenDiesenMonat} Zahlungen eingegangen`}
          icon={CreditCard}
          color="#059669"
          trend={kpis.zahlungenTrend ? (Number(kpis.zahlungenTrend) >= 0 ? 'up' : 'down') : 'neutral'}
          trendLabel={kpis.zahlungenTrend ? `${kpis.zahlungenTrend}%` : '—'}
        />
        <KPICard
          label="Offene Forderungen MW"
          value={formatEuro(kpis.forderungenBetrag)}
          subtext={`${kpis.offeneForderungen} Forderungen offen`}
          icon={Scale}
          color="#D97706"
        />
        <KPICard
          label="Überfällige Rechnungen"
          value={formatEuro(kpis.ueberfaelligBetrag)}
          subtext={`${kpis.ueberfaellige} Rechnungen überfällig`}
          icon={AlertCircle}
          color="#DC2626"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Umsatz Chart + Schnellzugriff */}
        <div className="col-span-1 lg:col-span-1 space-y-6">
          {/* Umsatz Chart */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Umsatz letzte 6 Monate</h3>
                <p className="text-xs text-gray-500 mt-0.5">Basierend auf Zahlungseingängen</p>
              </div>
              <BarChart3 className="w-4 h-4 text-gray-400" />
            </div>
            <MiniBarChart data={kpis.umsatzMonate} />
            <div className="mt-4 pt-3 border-t border-gray-100">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">Jahresumsatz {new Date().getFullYear()}</span>
                <span className="text-sm font-bold text-gray-900">{formatEuro(kpis.jahresUmsatz)}</span>
              </div>
            </div>
          </div>

          {/* Schnellzugriff */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Schnellzugriff</h3>
            <div className="space-y-2">
              {[
                { label: 'Rechnungen', desc: 'Alle Rechnungen verwalten', icon: Receipt, path: '/buchhaltung/rechnungen', color: '#2563EB' },
                { label: 'DATEV-Export', desc: 'CSV für Steuerberater', icon: Download, path: '/buchhaltung/datev', color: '#059669' },
                { label: 'Eingangskorb', desc: 'Eingangsbelege verwalten', icon: Receipt, path: '/buchhaltung/eingangskorb', color: '#2563EB' },
                { label: 'Buchhaltungsvorb.', desc: 'BWA & Buchungsvorschläge', icon: Building2, path: '/buchhaltung/vorbereitung', color: '#0D9488' },
                { label: 'Überweisungen', desc: 'Überweisungen & Freigaben', icon: CreditCard, path: '/buchhaltung/ueberweisungen', color: '#059669' },
                { label: 'Forderungsmanager', desc: 'Alle Betriebe — offene Posten', icon: Scale, path: null as string | null, action: openForderungsmanager, color: '#D97706' },
                { label: 'Buchhaltung', desc: 'Konten & Buchungen', icon: Building2, path: '/buchhaltung/konten', color: '#7C3AED' },
                { label: 'Fahrzeuge LCS', desc: 'LCS GmbH Fahrzeuge', icon: FileText, path: '/fahrzeuge?betrieb=lcs', color: '#0D9488' },
              ].map(item => (
                <button
                  key={item.label}
                  onClick={() => item.action ? item.action() : item.path && setLocation(item.path)}
                  className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors text-left group min-h-[44px]"
                >
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${item.color}12` }}>
                    <item.icon className="w-4 h-4" style={{ color: item.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900">{item.label}</div>
                    <div className="text-[10px] text-gray-500">{item.desc}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors" />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Letzte Rechnungen + Zahlungseingänge */}
        <div className="col-span-1 lg:col-span-2 space-y-6">
          {/* Letzte Rechnungen */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-900">Letzte Rechnungen</h3>
              <button
                onClick={() => setLocation('/buchhaltung/rechnungen')}
                className="flex items-center gap-1 text-xs text-[#C41E3A] font-medium hover:underline min-h-[44px]"
              >
                Alle anzeigen <ExternalLink className="w-3 h-3" />
              </button>
            </div>
            {letzteRechnungen.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">Noch keine Rechnungen vorhanden</div>
            ) : (
              <div className="space-y-1">
                {letzteRechnungen.map(r => {
                  const empfaenger = r.empfaenger_firma || r.empfaenger_name || (r as any).kunden?.firma || `${(r as any).kunden?.vorname || ''} ${(r as any).kunden?.nachname || ''}`.trim() || '—';
                  const restBetrag = r.brutto_betrag - r.bezahlt_betrag;
                  return (
                    <div key={r.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-gray-50 transition-colors">
                      <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <Receipt className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900 truncate">{r.rechnungs_nr}</span>
                          <StatusBadge status={r.status} />
                        </div>
                        <div className="text-xs text-gray-500 truncate">{empfaenger}</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-sm font-semibold text-gray-900">{formatEuro(r.brutto_betrag)}</div>
                        <div className="text-[10px] text-gray-400">{formatDate(r.rechnungsdatum)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Zahlungseingänge */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-900">Letzte Zahlungseingänge</h3>
              <button
                onClick={() => setLocation('/buchhaltung/konten')}
                className="flex items-center gap-1 text-xs text-[#C41E3A] font-medium hover:underline min-h-[44px]"
              >
                Alle anzeigen <ExternalLink className="w-3 h-3" />
              </button>
            </div>
            {letzteZahlungen.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">Noch keine Zahlungseingänge</div>
            ) : (
              <div className="space-y-1">
                {letzteZahlungen.map(z => (
                  <div key={z.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                      <CreditCard className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{z.verwendungszweck || 'Zahlung'}</div>
                      <div className="text-xs text-gray-500 truncate">{z.zahlungsart}</div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-sm font-semibold text-emerald-600">+{formatEuro(z.betrag)}</div>
                      <div className="text-[10px] text-gray-400">{formatDate(z.datum)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Forderungen MW Übersicht */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-900">Forderungen Mietwagen</h3>
              <button
                onClick={() => openForderungsmanager()}
                className="flex items-center gap-1 text-xs text-[#C41E3A] font-medium hover:underline min-h-[44px]"
              >
                Alle anzeigen <ExternalLink className="w-3 h-3" />
              </button>
            </div>
            {forderungen.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">Noch keine Forderungen</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="bg-amber-50/50 border border-amber-100 rounded-lg p-3 text-center">
                  <div className="text-lg font-bold text-amber-700">
                    {forderungen.filter(f => !['erledigt', 'abgerechnet'].includes(f.status)).length}
                  </div>
                  <div className="text-[10px] text-amber-600 font-medium">Offen</div>
                </div>
                <div className="bg-blue-50/50 border border-blue-100 rounded-lg p-3 text-center">
                  <div className="text-lg font-bold text-blue-700">
                    {forderungen.filter(f => f.status === 'in_bearbeitung').length}
                  </div>
                  <div className="text-[10px] text-blue-600 font-medium">In Bearbeitung</div>
                </div>
                <div className="bg-emerald-50/50 border border-emerald-100 rounded-lg p-3 text-center">
                  <div className="text-lg font-bold text-emerald-700">
                    {forderungen.filter(f => ['erledigt', 'abgerechnet'].includes(f.status)).length}
                  </div>
                  <div className="text-[10px] text-emerald-600 font-medium">Erledigt</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Liquidität — Saldo-Widgets */}
      <div className="mt-6 bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Wallet className="w-4 h-4 text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-900">Liquidität — Kontenübersicht</h3>
        </div>
        <LiquiditaetDashboardWidget />
      </div>
    </div>
  );
}
